export const M9_UNIT_15 = {
  id: "m9-u15-being-specific",
  title: "Unidad 15: Being Specific - Describing People & Jobs",
  topic: "Master WHO and THAT to describe people and things precisely! Learn defining relative clauses to talk about jobs, personalities, and characteristics. Essential for professional settings!",
  iconType: "users",
  grammarImage: "/grammar/grammar-m9-u15.jpg",
  grammarText: "Defining Relative Clauses! Use WHO for people (someone who is reliable). Use THAT for things (a job that is challenging). These clauses give ESSENTIAL information about what or who you're talking about!",
  
  sets: [
    {
      id: "m9-u15-s1",
      title: "Set 1: Introduction to WHO for People 👥",
      context: `**Job Interview**

Manager: "We're looking for someone who is reliable and hardworking. The person who gets this job will need excellent communication skills."`,
      
      questions: [
        {
          type: "mc",
          q: "WHO is used to describe:",
          options: [
            "People",
            "Things",
            "Places"
          ],
          a: "People",
          tip: "WHO = for people! THAT = for things!",
          successtip: "✓ WHO FOR PEOPLE!\nUse WHO when you're talking about a person! The relative pronoun for humans! 👤"
        },
        {
          type: "match",
          q: "Match the sentence parts:",
          pairs: [
            { left: "I know someone", right: "who speaks Chinese" },
            { left: "She's a person", right: "who loves adventure" },
            { left: "He's the man", right: "who helped me yesterday" }
          ],
          a: "completed",
          successtip: "✓ WHO CONNECTS!\nWHO links the person to their description! Essential information! 🔗"
        },
        {
          type: "input",
          q: "Complete: I'm looking for someone _____ can help me.",
          a: "who",
          alts: [],
          tip: "We're talking about a person!",
          successtip: "✓ WHO FOR PEOPLE!\nAlways use WHO when describing what kind of person you need! 💼"
        },
        {
          type: "order",
          q: "Translate: Conozco a alguien que habla japonés",
          words: ["I", "know", "someone", "who", "speaks", "Japanese"],
          a: "I know someone who speaks Japanese",
          extra: ["that", "which"],
          tip: "WHO for people!",
          successtip: "✓ PERFECT STRUCTURE!\nI know someone WHO + verb! This is how you specify which person! 🎯"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "She's the teacher who helped me",
            "She's the teacher which helped me",
            "She's the teacher what helped me"
          ],
          a: "She's the teacher who helped me",
          successtip: "✓ WHO FOR TEACHER!\nTeacher = person, so use WHO! Never WHICH or WHAT for people! ✨"
        },
        {
          type: "input",
          q: "Complete: He's the doctor _____ saved my life.",
          a: "who",
          alts: [],
          successtip: "✓ WHO FOR DOCTOR!\nDoctor = person! WHO is the only correct choice! 🏥"
        },
        {
          type: "match",
          q: "Match the job descriptions:",
          pairs: [
            { left: "A nurse is someone", right: "who cares for patients" },
            { left: "A chef is someone", right: "who cooks food" },
            { left: "A pilot is someone", right: "who flies planes" }
          ],
          a: "completed",
          successtip: "✓ DEFINING JOBS!\nWHO helps you define and describe professions! Professional English! 💼"
        },
        {
          type: "order",
          q: "Translate: Ella es una persona que ama los animales",
          words: ["She's", "a", "person", "who", "loves", "animals"],
          a: "She's a person who loves animals",
          extra: ["that", "which"],
          successtip: "✓ DESCRIBING PEOPLE!\nPerfect way to describe someone's characteristics! Natural English! 🐕"
        },
        {
          type: "mc",
          q: "In 'I need someone who is reliable', WHO introduces:",
          options: [
            "Essential information about what kind of person",
            "Extra optional information",
            "A question"
          ],
          a: "Essential information about what kind of person",
          successtip: "✓ ESSENTIAL INFO!\nWHO introduces a DEFINING clause - you NEED this info to know which person! 🎯"
        },
        {
          type: "input",
          q: "Complete: We're looking for people _____ work hard.",
          a: "who",
          alts: [],
          tip: "People = WHO!",
          successtip: "✓ WHO FOR PEOPLE!\nEven in plural! WHO works for one person or many people! 👥"
        }
      ]
    },
    
    {
      id: "m9-u15-s2",
      title: "Set 2: Using THAT for Things 📦",
      context: `**Office Discussion**

Manager: "I need a project that is challenging. I want a job that uses my skills and a career that offers growth opportunities."`,
      
      questions: [
        {
          type: "mc",
          q: "THAT is used for:",
          options: [
            "Things and objects",
            "Only people",
            "Only animals"
          ],
          a: "Things and objects",
          tip: "THAT = things, WHO = people!",
          successtip: "✓ THAT FOR THINGS!\nUse THAT when you're talking about objects, jobs, projects, ideas! 📦"
        },
        {
          type: "match",
          q: "Match the sentence parts:",
          pairs: [
            { left: "I want a job", right: "that is interesting" },
            { left: "This is the book", right: "that I recommended" },
            { left: "She bought a car", right: "that costs a lot" }
          ],
          a: "completed",
          successtip: "✓ THAT DESCRIBES!\nTHAT links things to their characteristics! Defining what you mean! 🔗"
        },
        {
          type: "input",
          q: "Complete: I need a laptop _____ is fast.",
          a: "that",
          alts: [],
          tip: "Laptop = thing!",
          successtip: "✓ THAT FOR LAPTOP!\nLaptop is a thing, so use THAT! Simple rule! 💻"
        },
        {
          type: "order",
          q: "Translate: Quiero un trabajo que sea desafiante",
          words: ["I", "want", "a", "job", "that", "is", "challenging"],
          a: "I want a job that is challenging",
          extra: ["who", "which"],
          tip: "Job = thing!",
          successtip: "✓ PERFECT STRUCTURE!\nI want a job THAT + adjective! Describing what kind of job! 💼"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "I need a phone that has a good camera",
            "I need a phone who has a good camera",
            "I need a phone what has a good camera"
          ],
          a: "I need a phone that has a good camera",
          successtip: "✓ THAT FOR PHONE!\nPhone = object, use THAT! Never WHO for things! 📱"
        },
        {
          type: "input",
          q: "Complete: This is the project _____ I'm working on.",
          a: "that",
          alts: [],
          successtip: "✓ THAT FOR PROJECT!\nProject = thing! THAT is correct! 📊"
        },
        {
          type: "match",
          q: "Match the business descriptions:",
          pairs: [
            { left: "A report that is detailed", right: "will be useful" },
            { left: "A meeting that is productive", right: "saves time" },
            { left: "A strategy that works", right: "brings success" }
          ],
          a: "completed",
          successtip: "✓ BUSINESS LANGUAGE!\nTHAT helps you describe professional things precisely! Corporate English! 💼"
        },
        {
          type: "order",
          q: "Translate: Compré un coche que es muy rápido",
          words: ["I", "bought", "a", "car", "that", "is", "very", "fast"],
          a: "I bought a car that is very fast",
          extra: ["who", "which"],
          successtip: "✓ DESCRIBING PURCHASES!\nPerfect for talking about things you own! Natural structure! 🚗"
        },
        {
          type: "mc",
          q: "In 'I found a house that I love', the clause 'that I love':",
          options: [
            "Specifies which house we're talking about",
            "Is extra information we don't need",
            "Asks a question"
          ],
          a: "Specifies which house we're talking about",
          successtip: "✓ DEFINING CLAUSE!\nTHAT I love tells us WHICH house - essential information! 🏠"
        },
        {
          type: "input",
          q: "Complete: She's looking for a dress _____ fits perfectly.",
          a: "that",
          alts: [],
          tip: "Dress = thing!",
          successtip: "✓ THAT FOR DRESS!\nClothing items = things, use THAT! Perfect grammar! 👗"
        }
      ]
    },

    {
      id: "m9-u15-s3",
      title: "Set 3: Personal Characteristics Vocabulary 🌟",
      context: `**Job Posting**

"We need someone who is **reliable**, **confident**, and **cheerful**. The ideal candidate should be **caring**, **efficient**, and **calm** under pressure."`,
      
      questions: [
        {
          type: "mc",
          q: "What does 'reliable' mean?",
          options: [
            "Someone you can trust and depend on",
            "Someone who is funny",
            "Someone who is shy"
          ],
          a: "Someone you can trust and depend on",
          tip: "Think: can you rely on them?",
          successtip: "✓ RELIABLE!\nSomeone who always does what they promise! Dependable and trustworthy! 🤝"
        },
        {
          type: "match",
          q: "Match the characteristics to their meanings:",
          pairs: [
            { left: "Confident", right: "Sure about your abilities" },
            { left: "Cheerful", right: "Happy and positive" },
            { left: "Caring", right: "Kind and thoughtful to others" }
          ],
          a: "completed",
          successtip: "✓ POSITIVE TRAITS!\nThese are all desirable characteristics in professional settings! ⭐"
        },
        {
          type: "input",
          q: "Complete: Someone who stays _____ doesn't panic in emergencies.",
          a: "calm",
          alts: [],
          tip: "Not excited or nervous...",
          successtip: "✓ CALM!\nEssential quality for high-pressure jobs! Cool under pressure! 😌"
        },
        {
          type: "order",
          q: "Create a sentence: needs / who / efficient / is / We / someone",
          words: ["We", "need", "someone", "who", "is", "efficient"],
          a: "We need someone who is efficient",
          extra: ["that", "which"],
          successtip: "✓ JOB REQUIREMENTS!\nPerfect sentence for describing what kind of employee you need! 💼"
        },
        {
          type: "mc",
          q: "An 'outgoing' person is:",
          options: [
            "Friendly and sociable",
            "Quiet and shy",
            "Always leaving"
          ],
          a: "Friendly and sociable",
          successtip: "✓ OUTGOING!\nSomeone who enjoys meeting people and being social! Extroverted! 🎉"
        },
        {
          type: "match",
          q: "Match opposites:",
          pairs: [
            { left: "Confident", right: "Shy" },
            { left: "Calm", right: "Stressed" },
            { left: "Cheerful", right: "Grumpy" }
          ],
          a: "completed",
          successtip: "✓ UNDERSTANDING OPPOSITES!\nKnowing opposites helps you describe people precisely! 🔄"
        },
        {
          type: "input",
          q: "Complete: Someone who works in an organized way is _____.",
          a: "efficient",
          alts: [],
          tip: "Gets things done well...",
          successtip: "✓ EFFICIENT!\nDoes work quickly and well without wasting time! Productive! ⚡"
        },
        {
          type: "order",
          q: "Translate: Buscamos a alguien que sea confiable",
          words: ["We're", "looking", "for", "someone", "who", "is", "reliable"],
          a: "We're looking for someone who is reliable",
          extra: ["that", "confident"],
          successtip: "✓ PROFESSIONAL LANGUAGE!\nPerfect for job interviews and hiring situations! 📋"
        },
        {
          type: "mc",
          q: "Someone with a 'good sense of humor':",
          options: [
            "Appreciates funny things and can laugh",
            "Is always serious",
            "Never smiles"
          ],
          a: "Appreciates funny things and can laugh",
          successtip: "✓ SENSE OF HUMOR!\nCan see the funny side of things! Makes work enjoyable! 😄"
        },
        {
          type: "match",
          q: "Match the job to the key characteristic:",
          pairs: [
            { left: "Nurse", right: "Caring" },
            { left: "Manager", right: "Confident" },
            { left: "Teacher", right: "Cheerful" }
          ],
          a: "completed",
          tip: "Think about what each job needs!",
          successtip: "✓ JOB-SPECIFIC TRAITS!\nDifferent jobs require different characteristics! Match skills to careers! 👔"
        }
      ]
    },

    {
      id: "m9-u15-s4",
      title: "Set 4: More Personal Characteristics 💪",
      context: `**Performance Review**

"Sarah is someone who is **fun-loving** and has a **good sense of humor**. She's also **conscientious** and **humble**, which makes her perfect for teamwork."`,
      
      questions: [
        {
          type: "mc",
          q: "A 'conscientious' person:",
          options: [
            "Wants to do their job well and carefully",
            "Doesn't care about quality",
            "Is always late"
          ],
          a: "Wants to do their job well and carefully",
          tip: "Takes work seriously!",
          successtip: "✓ CONSCIENTIOUS!\nTakes pride in doing quality work! Thorough and careful! 📝"
        },
        {
          type: "match",
          q: "Match the words to their meanings:",
          pairs: [
            { left: "Humble", right: "Not arrogant or boastful" },
            { left: "Fun-loving", right: "Enjoys having a good time" },
            { left: "Self-confident", right: "Believes in their own abilities" }
          ],
          a: "completed",
          successtip: "✓ PERSONALITY TRAITS!\nThese describe how people behave and think! Character vocabulary! 🌟"
        },
        {
          type: "input",
          q: "Complete: Someone who is not shy and believes in themselves is _____.",
          a: "self-confident",
          alts: ["confident"],
          tip: "They have confidence in themselves...",
          successtip: "✓ SELF-CONFIDENT!\nBelieve in their own abilities! Not afraid to try new things! 💪"
        },
        {
          type: "order",
          q: "Create: teacher / is / who / We / a / need / patient",
          words: ["We", "need", "a", "teacher", "who", "is", "patient"],
          a: "We need a teacher who is patient",
          extra: ["that", "which"],
          successtip: "✓ HIRING LANGUAGE!\nDescribing the perfect candidate for a position! Professional! 👨‍🏫"
        },
        {
          type: "mc",
          q: "'Polite' means:",
          options: [
            "Well-mannered and respectful",
            "Rude and aggressive",
            "Quiet and silent"
          ],
          a: "Well-mannered and respectful",
          successtip: "✓ POLITE!\nShows good manners and respect to others! Essential social skill! 🙏"
        },
        {
          type: "input",
          q: "Complete: A person who is modest and not arrogant is _____.",
          a: "humble",
          alts: [],
          tip: "Opposite of arrogant...",
          successtip: "✓ HUMBLE!\nDoesn't boast about achievements! Down-to-earth! 🌱"
        },
        {
          type: "match",
          q: "Match the workplace characteristics:",
          pairs: [
            { left: "A colleague who helps others", right: "is caring" },
            { left: "A boss who trusts their team", right: "is confident" },
            { left: "A worker who checks details", right: "is conscientious" }
          ],
          a: "completed",
          successtip: "✓ WORKPLACE BEHAVIOR!\nHow these traits show up in professional settings! 💼"
        },
        {
          type: "order",
          q: "Translate: Tiene un gran sentido del humor",
          words: ["She", "has", "a", "great", "sense", "of", "humor"],
          a: "She has a great sense of humor",
          extra: ["good", "who"],
          successtip: "✓ DESCRIBING PERSONALITY!\nTalking about someone's character! Natural expression! 😊"
        },
        {
          type: "mc",
          q: "In 'He's someone who is fun-loving', this means:",
          options: [
            "He enjoys having a good time and likes fun activities",
            "He only loves fun and nothing else",
            "He is in love with fun"
          ],
          a: "He enjoys having a good time and likes fun activities",
          successtip: "✓ FUN-LOVING!\nLikes to enjoy life and have fun! Positive, energetic personality! 🎊"
        },
        {
          type: "match",
          q: "Match the descriptions to characteristics:",
          pairs: [
            { left: "Always on time, never late", right: "Reliable" },
            { left: "Stays cool under pressure", right: "Calm" },
            { left: "Always smiling and positive", right: "Cheerful" }
          ],
          a: "completed",
          successtip: "✓ BEHAVIOR PATTERNS!\nRecognizing how traits show in everyday actions! Practical! ✨"
        }
      ]
    },

    {
      id: "m9-u15-s5",
      title: "Set 5: Describing Jobs with Relative Clauses 💼",
      context: `**Career Fair**

Recruiter: "We have a job that is challenging and rewarding. We need people who are efficient and reliable. This is a career that offers excellent growth opportunities."`,
      
      questions: [
        {
          type: "mc",
          q: "Which sentence correctly describes a job requirement?",
          options: [
            "We need someone who speaks Spanish",
            "We need someone that speaks Spanish",
            "We need someone which speaks Spanish"
          ],
          a: "We need someone who speaks Spanish",
          tip: "Someone = person!",
          successtip: "✓ WHO FOR PEOPLE!\nPerson = WHO! THAT can also work, but WHO is more precise for people! 👤"
        },
        {
          type: "match",
          q: "Match the job descriptions:",
          pairs: [
            { left: "A doctor is someone", right: "who treats patients" },
            { left: "An engineer is someone", right: "who designs buildings" },
            { left: "A lawyer is someone", right: "who knows the law" }
          ],
          a: "completed",
          successtip: "✓ DEFINING PROFESSIONS!\nUsing WHO to explain what different jobs involve! Professional vocabulary! 🎓"
        },
        {
          type: "input",
          q: "Complete: I want a job _____ is interesting and well-paid.",
          a: "that",
          alts: [],
          tip: "Job = thing!",
          successtip: "✓ THAT FOR JOB!\nJob is a thing/concept, so use THAT! Talking about career goals! 💼"
        },
        {
          type: "order",
          q: "Translate: Busco un trabajo que sea desafiante",
          words: ["I'm", "looking", "for", "a", "job", "that", "is", "challenging"],
          a: "I'm looking for a job that is challenging",
          extra: ["who", "which"],
          successtip: "✓ JOB HUNTING!\nPerfect sentence for talking about your career goals! Professional English! 🎯"
        },
        {
          type: "mc",
          q: "Which describes a workplace quality?",
          options: [
            "We need an office that has good lighting",
            "We need an office who has good lighting",
            "We need an office what has good lighting"
          ],
          a: "We need an office that has good lighting",
          successtip: "✓ THAT FOR OFFICE!\nOffice = place/thing, use THAT! Describing physical spaces! 🏢"
        },
        {
          type: "match",
          q: "Match job types to descriptions:",
          pairs: [
            { left: "A job that is creative", right: "uses imagination" },
            { left: "A job that is demanding", right: "requires hard work" },
            { left: "A job that is rewarding", right: "gives satisfaction" }
          ],
          a: "completed",
          successtip: "✓ JOB QUALITIES!\nDescribing different types of work! Career vocabulary! 📊"
        },
        {
          type: "input",
          q: "Complete: They're hiring someone _____ can work weekends.",
          a: "who",
          alts: [],
          tip: "Someone = person!",
          successtip: "✓ WHO FOR SOMEONE!\nAlways use WHO when 'someone' refers to a person! 👥"
        },
        {
          type: "order",
          q: "Create: company / for / works / who / a / I / someone / know / big",
          words: ["I", "know", "someone", "who", "works", "for", "a", "big", "company"],
          a: "I know someone who works for a big company",
          extra: ["that", "which"],
          successtip: "✓ NETWORKING LANGUAGE!\nTalking about professional connections! Business English! 🤝"
        },
        {
          type: "mc",
          q: "In professional contexts, 'a position that offers growth' means:",
          options: [
            "A job with opportunities for advancement",
            "A job that is getting bigger physically",
            "A job in a garden"
          ],
          a: "A job with opportunities for advancement",
          successtip: "✓ CAREER GROWTH!\nOpportunities to advance and develop professionally! Corporate language! 📈"
        },
        {
          type: "match",
          q: "Complete the job ads:",
          pairs: [
            { left: "Seeking a candidate who", right: "is bilingual" },
            { left: "Looking for a position that", right: "offers flexibility" },
            { left: "Need an assistant who", right: "can multitask" }
          ],
          a: "completed",
          successtip: "✓ RECRUITMENT LANGUAGE!\nHow job ads are written! Professional hiring vocabulary! 📋"
        }
      ]
    },

    {
      id: "m9-u15-s6",
      title: "Set 6: Complex Defining Clauses 🎯",
      context: `**Real Situations**

"The job that I heard about is perfect for me. I know someone who can help you. The project that we discussed will start next month."`,
      
      questions: [
        {
          type: "mc",
          q: "In 'The job that I heard about', which word can be removed?",
          options: [
            "THAT can be removed (The job I heard about)",
            "HEARD can be removed",
            "Nothing can be removed"
          ],
          a: "THAT can be removed (The job I heard about)",
          tip: "When THAT is the object, it's optional!",
          successtip: "✓ OPTIONAL THAT!\nWhen THAT is the object of the verb, you can omit it! Both ways correct! ✨"
        },
        {
          type: "match",
          q: "Match the complete sentences:",
          pairs: [
            { left: "The person that I met", right: "was very friendly" },
            { left: "The book that she wrote", right: "became a bestseller" },
            { left: "The house that they bought", right: "needs renovation" }
          ],
          a: "completed",
          successtip: "✓ COMPLEX STRUCTURES!\nDefining clauses can be longer and more detailed! Advanced grammar! 🔧"
        },
        {
          type: "input",
          q: "Complete: The woman _____ works here is my sister.",
          a: "who",
          alts: [],
          tip: "Woman = person!",
          successtip: "✓ WHO FOR WOMAN!\nWHO is the subject here - can't be omitted! Essential! 👩"
        },
        {
          type: "order",
          q: "Create: met / who / yesterday / I / The / left / person",
          words: ["The", "person", "who", "I", "met", "yesterday", "left"],
          a: "The person who I met yesterday left",
          extra: ["that", "which"],
          successtip: "✓ TIME EXPRESSIONS!\nCan include time markers in relative clauses! Natural flow! ⏰"
        },
        {
          type: "mc",
          q: "Which is more natural in conversation?",
          options: [
            "The movie I saw (omitting THAT)",
            "The movie that I saw",
            "Both are equally natural"
          ],
          a: "The movie I saw (omitting THAT)",
          tip: "Native speakers often drop THAT!",
          successtip: "✓ NATURAL ENGLISH!\nNative speakers often omit THAT when it's the object! Sounds more natural! 🗣️"
        },
        {
          type: "match",
          q: "Which can drop THAT/WHO?",
          pairs: [
            { left: "The car (that) I bought", right: "CAN drop - THAT is object" },
            { left: "The man who called", right: "CANNOT drop - WHO is subject" },
            { left: "The food (that) you made", right: "CAN drop - THAT is object" }
          ],
          a: "completed",
          tip: "Subject WHO/THAT = must stay. Object WHO/THAT = optional!",
          successtip: "✓ SUBJECT VS OBJECT!\nBig rule: Subject pronouns must stay, object pronouns are optional! 📚"
        },
        {
          type: "input",
          q: "Complete: The project _____ we finished was successful.",
          a: "that",
          alts: [""],
          tip: "Both 'that' and nothing work!",
          successtip: "✓ OPTIONAL THAT!\nTHAT is the object here, so you can include it or drop it! Both correct! ✅"
        },
        {
          type: "order",
          q: "Translate: El libro que compré es interesante",
          words: ["The", "book", "I", "bought", "is", "interesting"],
          a: "The book I bought is interesting",
          extra: ["that", "which"],
          tip: "THAT is optional here!",
          successtip: "✓ NATURAL OMISSION!\nDropping THAT makes it sound more natural and fluent! Native-like! 🎯"
        },
        {
          type: "mc",
          q: "In 'The woman who lives here', can we drop WHO?",
          options: [
            "No, because WHO is the subject",
            "Yes, we can always drop WHO",
            "Only in formal writing"
          ],
          a: "No, because WHO is the subject",
          successtip: "✓ SUBJECT WHO!\nWhen WHO/THAT is the subject, you MUST keep it! Can't drop it! ⚠️"
        },
        {
          type: "match",
          q: "Identify the structure:",
          pairs: [
            { left: "The phone (that) I want", right: "Object - THAT optional" },
            { left: "The phone that rings", right: "Subject - THAT required" },
            { left: "The person (who) you met", right: "Object - WHO optional" }
          ],
          a: "completed",
          successtip: "✓ MASTER RULE!\nSubject relative pronouns = keep them! Object relative pronouns = optional! 🏆"
        }
      ]
    },

    {
      id: "m9-u15-s7",
      title: "Set 7: Real-World Applications 🌍",
      context: `**Networking Event**

"I'm working on a project that combines technology and education. I'm looking for partners who share my vision. This is an opportunity that could change everything!"`,
      
      questions: [
        {
          type: "mc",
          q: "How would you describe your ideal job?",
          options: [
            "I want a job that is challenging and rewarding",
            "I want a job who is challenging and rewarding",
            "I want a job which challenges and rewards"
          ],
          a: "I want a job that is challenging and rewarding",
          successtip: "✓ CAREER GOALS!\nPerfect way to talk about your professional aspirations! 💼"
        },
        {
          type: "match",
          q: "Match the networking phrases:",
          pairs: [
            { left: "I'm someone who", right: "loves solving problems" },
            { left: "I need a role that", right: "lets me be creative" },
            { left: "I work with people who", right: "inspire me daily" }
          ],
          a: "completed",
          successtip: "✓ NETWORKING LANGUAGE!\nEssential phrases for professional networking events! 🤝"
        },
        {
          type: "input",
          q: "Complete: I'm looking for a mentor _____ can guide me.",
          a: "who",
          alts: [],
          tip: "Mentor = person!",
          successtip: "✓ FINDING MENTORS!\nDescribing what kind of professional guidance you need! 👨‍🏫"
        },
        {
          type: "order",
          q: "Create your elevator pitch: passion / a / have / that / technology / I / for / drives / me",
          words: ["I", "have", "a", "passion", "for", "technology", "that", "drives", "me"],
          a: "I have a passion for technology that drives me",
          extra: ["who", "which"],
          tip: "Passion = thing!",
          successtip: "✓ ELEVATOR PITCH!\nDescribing what motivates you professionally! Career language! 🚀"
        },
        {
          type: "mc",
          q: "At a job interview, you should say:",
          options: [
            "I'm someone who works well under pressure",
            "I'm someone that works well under pressure",
            "I'm someone what works well under pressure"
          ],
          a: "I'm someone who works well under pressure",
          tip: "WHO is more precise for people!",
          successtip: "✓ INTERVIEW LANGUAGE!\nPerfect self-description for professional interviews! Impressive! 💯"
        },
        {
          type: "match",
          q: "Match the professional qualities:",
          pairs: [
            { left: "A team member who", right: "communicates clearly" },
            { left: "A leader who", right: "inspires others" },
            { left: "A colleague who", right: "meets deadlines" }
          ],
          a: "completed",
          successtip: "✓ TEAM DYNAMICS!\nDescribing ideal colleagues and team members! Workplace language! 👥"
        },
        {
          type: "input",
          q: "Complete: She's the kind of manager _____ everyone respects.",
          a: "who",
          alts: ["that"],
          tip: "Manager = person!",
          successtip: "✓ DESCRIBING LEADERSHIP!\nTalking about management styles and qualities! Professional! 👔"
        },
        {
          type: "order",
          q: "Translate: Trabajo en proyectos que marcan la diferencia",
          words: ["I", "work", "on", "projects", "that", "make", "a", "difference"],
          a: "I work on projects that make a difference",
          extra: ["who", "which"],
          successtip: "✓ MEANINGFUL WORK!\nDescribing work that has impact! Inspiring professional language! ⭐"
        },
        {
          type: "mc",
          q: "To describe your company, you'd say:",
          options: [
            "We're a company that values innovation",
            "We're a company who values innovation",
            "We're a company what values innovation"
          ],
          a: "We're a company that values innovation",
          successtip: "✓ COMPANY CULTURE!\nDescribing organizational values and culture! Corporate communication! 🏢"
        },
        {
          type: "match",
          q: "Complete the professional profiles:",
          pairs: [
            { left: "An entrepreneur who", right: "takes calculated risks" },
            { left: "A business that", right: "prioritizes customers" },
            { left: "A strategy that", right: "focuses on growth" }
          ],
          a: "completed",
          successtip: "✓ BUSINESS PROFILES!\nDescribing businesses, strategies, and professionals! Complete mastery! 🎯"
        }
      ]
    },

    {
      id: "m9-u15-s8",
      title: "Set 8: WHO vs THAT - Making the Right Choice 🤔",
      context: `**Grammar Rules**

"Remember: WHO is for people. THAT can be for people OR things. WHICH is only for things in defining clauses (less common in speaking)."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is grammatically correct for people?",
          options: [
            "Both 'someone who' and 'someone that' are correct",
            "Only 'someone who' is correct",
            "Only 'someone that' is correct"
          ],
          a: "Both 'someone who' and 'someone that' are correct",
          tip: "THAT can be used for people too!",
          successtip: "✓ FLEXIBLE RULE!\nWHO is preferred for people, but THAT also works! WHO is more precise! 👤"
        },
        {
          type: "match",
          q: "Match the best pronoun choice:",
          pairs: [
            { left: "People - Most natural", right: "WHO" },
            { left: "Things - Always correct", right: "THAT" },
            { left: "Both people & things", right: "THAT works for both" }
          ],
          a: "completed",
          successtip: "✓ PRONOUN HIERARCHY!\nWHO = people (best). THAT = things (best) or people (OK). WHICH = things (formal)! 📊"
        },
        {
          type: "input",
          q: "Complete with the BEST choice: A teacher _____ inspires students is valuable.",
          a: "who",
          alts: ["that"],
          tip: "Both work, but one is more precise!",
          successtip: "✓ WHO IS BEST!\nFor people, WHO is the most precise and natural choice! 👨‍🏫"
        },
        {
          type: "order",
          q: "Use the most natural pronoun: colleagues / I / with / work / supportive / are / who",
          words: ["The", "colleagues", "who", "I", "work", "with", "are", "supportive"],
          a: "The colleagues who I work with are supportive",
          extra: ["that", "which"],
          successtip: "✓ NATURAL CHOICE!\nWHO sounds most natural when talking about people! Professional! 💼"
        },
        {
          type: "mc",
          q: "For objects and things, which is MOST common in speaking?",
          options: [
            "THAT",
            "WHICH",
            "WHO"
          ],
          a: "THAT",
          tip: "WHICH is more formal!",
          successtip: "✓ THAT FOR THINGS!\nTHAT is the most common and natural choice for things in speech! 📦"
        },
        {
          type: "match",
          q: "Match the formality levels:",
          pairs: [
            { left: "The person who called", right: "Neutral - Standard" },
            { left: "The person that called", right: "Informal - Casual" },
            { left: "The thing which happened", right: "Formal - Written" }
          ],
          a: "completed",
          successtip: "✓ STYLE AWARENESS!\nWHO/THAT are neutral. WHICH is more formal in defining clauses! 🎩"
        },
        {
          type: "input",
          q: "Complete naturally: The car _____ I drive is old.",
          a: "that",
          alts: ["which", ""],
          tip: "THAT or nothing - both natural!",
          successtip: "✓ FLEXIBLE OPTIONS!\nFor objects, THAT works great, or you can drop it completely! 🚗"
        },
        {
          type: "order",
          q: "Most natural: software / use / we / efficient / is / that",
          words: ["The", "software", "that", "we", "use", "is", "efficient"],
          a: "The software that we use is efficient",
          extra: ["which", "who"],
          successtip: "✓ TECH VOCABULARY!\nTHAT works perfectly for technology and tools! 💻"
        },
        {
          type: "mc",
          q: "In formal writing for things, which is often preferred?",
          options: [
            "WHICH",
            "THAT",
            "WHO"
          ],
          a: "WHICH",
          tip: "Academic and formal texts use WHICH more!",
          successtip: "✓ FORMAL WRITING!\nWHICH is preferred in academic and formal writing for things! 📚"
        },
        {
          type: "match",
          q: "Choose the most appropriate pronoun:",
          pairs: [
            { left: "Students (speaking)", right: "who" },
            { left: "Equipment (speaking)", right: "that" },
            { left: "Research (academic)", right: "which" }
          ],
          a: "completed",
          successtip: "✓ CONTEXT MATTERS!\nChoose pronouns based on context: speaking vs writing, formal vs informal! 🎯"
        }
      ]
    },

    {
      id: "m9-u15-s9",
      title: "Set 9: Common Mistakes to Avoid ⚠️",
      context: `**Error Correction**

"Common mistakes: Using WHAT instead of WHO/THAT, using WHICH for people, forgetting to drop object pronouns naturally, and mixing up subject/object positions."`,
      
      questions: [
        {
          type: "mc",
          q: "Which sentence is WRONG?",
          options: [
            "The person what called me",
            "The person who called me",
            "The person that called me"
          ],
          a: "The person what called me",
          tip: "WHAT is not a relative pronoun!",
          successtip: "✓ NO WHAT!\nNever use WHAT as a relative pronoun! Use WHO, THAT, or WHICH! ❌"
        },
        {
          type: "match",
          q: "Match the error to the correction:",
          pairs: [
            { left: "The man which helped me", right: "The man WHO helped me" },
            { left: "Someone what I know", right: "Someone WHO I know" },
            { left: "The girl where works here", right: "The girl WHO works here" }
          ],
          a: "completed",
          successtip: "✓ COMMON CORRECTIONS!\nRecognizing and fixing these errors makes you sound professional! ✅"
        },
        {
          type: "input",
          q: "Correct this: The teacher what taught me was great.\nThe teacher _____ taught me was great.",
          a: "who",
          alts: ["that"],
          tip: "Replace WHAT!",
          successtip: "✓ WHO/THAT NOT WHAT!\nWHAT is never used as a relative pronoun! Use WHO or THAT! 👨‍🏫"
        },
        {
          type: "order",
          q: "Fix and arrange: book / what / The / was / read / I / interesting",
          words: ["The", "book", "that", "I", "read", "was", "interesting"],
          a: "The book that I read was interesting",
          extra: ["what", "which"],
          tip: "Remove WHAT, use THAT!",
          successtip: "✓ ERROR FIXED!\nReplaced WHAT with THAT! Perfect correction! 📚"
        },
        {
          type: "mc",
          q: "Which is incorrect?",
          options: [
            "The lady which sold me the car",
            "The lady who sold me the car",
            "The lady that sold me the car"
          ],
          a: "The lady which sold me the car",
          tip: "WHICH is for things, not people!",
          successtip: "✓ NO WHICH FOR PEOPLE!\nWHICH is only for things! Use WHO or THAT for people! 👩"
        },
        {
          type: "match",
          q: "Identify the problems:",
          pairs: [
            { left: "WHERE instead of WHO", right: "Wrong relative pronoun" },
            { left: "Keeping object THAT unnecessarily", right: "Too formal/unnatural" },
            { left: "Dropping subject WHO", right: "Grammatically wrong" }
          ],
          a: "completed",
          successtip: "✓ ERROR AWARENESS!\nKnowing these common mistakes helps you avoid them! 🎯"
        },
        {
          type: "input",
          q: "What's wrong with: 'The people works here are nice'?\nMissing word: _____",
          a: "who",
          alts: ["that"],
          tip: "Need a relative pronoun!",
          successtip: "✓ MISSING PRONOUN!\nNeed WHO or THAT to connect the clause! Essential grammar! 👥"
        },
        {
          type: "order",
          q: "Correct: students / which / The / study / here / international / are",
          words: ["The", "students", "who", "study", "here", "are", "international"],
          a: "The students who study here are international",
          extra: ["which", "what"],
          tip: "Students = people!",
          successtip: "✓ WHICH → WHO!\nChanged WHICH to WHO for people! Perfect correction! 🎓"
        },
        {
          type: "mc",
          q: "The biggest mistake English learners make:",
          options: [
            "Using WHAT as a relative pronoun",
            "Using WHO for people",
            "Using THAT for things"
          ],
          a: "Using WHAT as a relative pronoun",
          tip: "WHAT is not WHO, WHICH, or THAT!",
          successtip: "✓ AVOID WHAT!\nThis is the #1 error! WHAT is NOT a relative pronoun in defining clauses! ⚠️"
        },
        {
          type: "match",
          q: "Fix these sentences:",
          pairs: [
            { left: "The job what I want", right: "The job that I want" },
            { left: "People which work hard", right: "People who work hard" },
            { left: "The girl where I met", right: "The girl who I met" }
          ],
          a: "completed",
          successtip: "✓ COMPREHENSIVE FIXES!\nYou can now identify and correct all major relative clause errors! 🏆"
        }
      ]
    },

    {
      id: "m9-u15-s10",
      title: "Set 10: Mastery Challenge 🏆",
      context: `**Final Practice**

"Time to put it all together! Use WHO for people, THAT for things, remember when you can drop pronouns, and avoid common mistakes!"`,
      
      questions: [
        {
          type: "mc",
          q: "Choose the BEST completion: 'I'm searching for a career _____'",
          options: [
            "that combines my interests and skills",
            "who combines my interests and skills",
            "what combines my interests and skills"
          ],
          a: "that combines my interests and skills",
          successtip: "✓ CAREER PLANNING!\nTHAT for career (thing)! Perfect choice! 💼"
        },
        {
          type: "match",
          q: "Complete these professional statements:",
          pairs: [
            { left: "I'm the type of person who", right: "never gives up" },
            { left: "I have experience that", right: "makes me qualified" },
            { left: "I seek opportunities that", right: "challenge me" }
          ],
          a: "completed",
          successtip: "✓ PROFESSIONAL IDENTITY!\nPerfect for resumes, interviews, and networking! 🌟"
        },
        {
          type: "input",
          q: "Complete naturally: The colleague _____ sits next to me is helpful.",
          a: "who",
          alts: ["that"],
          tip: "Colleague = person!",
          successtip: "✓ WORKPLACE DESCRIPTION!\nWHO for colleague! Natural and correct! 👥"
        },
        {
          type: "order",
          q: "Create: values / company / employees / its / a / that / I / for / work",
          words: ["I", "work", "for", "a", "company", "that", "values", "its", "employees"],
          a: "I work for a company that values its employees",
          extra: ["who", "which"],
          successtip: "✓ COMPANY CULTURE!\nDescribing your workplace values! Professional statement! 🏢"
        },
        {
          type: "mc",
          q: "Most natural: 'The presentation _____ I prepared was successful'",
          options: [
            "I prepared (drop THAT)",
            "that I prepared",
            "which I prepared"
          ],
          a: "I prepared (drop THAT)",
          tip: "Dropping THAT sounds most natural!",
          successtip: "✓ NATURAL SPEECH!\nDropping object THAT sounds most fluent! Native-like! 🗣️"
        },
        {
          type: "match",
          q: "Match to create perfect job interview answers:",
          pairs: [
            { left: "I'm someone who", right: "learns quickly from feedback" },
            { left: "I have skills that", right: "match this position perfectly" },
            { left: "I want a role that", right: "allows me to grow" }
          ],
          a: "completed",
          successtip: "✓ INTERVIEW EXCELLENCE!\nPerfect answers for job interviews! Impressive language! 💯"
        },
        {
          type: "input",
          q: "Complete: The project _____ our team completed won an award.",
          a: "that",
          alts: ["which", ""],
          tip: "Project = thing. THAT or nothing!",
          successtip: "✓ ACHIEVEMENT LANGUAGE!\nDescribing accomplishments! Professional vocabulary! 🏆"
        },
        {
          type: "order",
          q: "Translate: Conocí a alguien que trabaja en Google",
          words: ["I", "met", "someone", "who", "works", "at", "Google"],
          a: "I met someone who works at Google",
          extra: ["that", "which"],
          successtip: "✓ NETWORKING!\nTalking about professional connections! Perfect structure! 🤝"
        },
        {
          type: "mc",
          q: "For 'candidates _____ apply must have experience', the best choice is:",
          options: [
            "who (subject, cannot be dropped)",
            "that (object, can be dropped)",
            "which (for people)"
          ],
          a: "who (subject, cannot be dropped)",
          tip: "WHO is the subject here!",
          successtip: "✓ SUBJECT PRONOUN!\nWHO is the subject - must keep it! Grammatically essential! ✅"
        },
        {
          type: "match",
          q: "Final mastery - identify all correctly:",
          pairs: [
            { left: "People who inspire me", right: "✓ WHO for people - subject" },
            { left: "The job I love", right: "✓ Dropped THAT - object" },
            { left: "A career that fits me", right: "✓ THAT for thing - subject" }
          ],
          a: "completed",
          successtip: "✓ COMPLETE MASTERY!\nYou understand all rules! WHO, THAT, dropping pronouns! Champion! 🏆"
        }
      ]
    },

    {
      id: "m9-u15-reading",
      title: "📖 Reading: Job Advertisement",
      context: `Read this job posting carefully and answer the questions:

**SENIOR MARKETING MANAGER - TECH STARTUP**

We're looking for someone who is creative, reliable, and self-confident. The ideal candidate is a person who has at least 5 years of experience in digital marketing and who can lead a team effectively.

This is a position that offers excellent growth opportunities. We need someone who is comfortable working in a fast-paced environment and who can handle multiple projects simultaneously.

The person who gets this job will work on campaigns that reach millions of users. You'll collaborate with colleagues who are passionate about technology and innovation.

**Requirements:**
- A professional who has managed social media campaigns
- Someone who is fluent in English and Spanish
- A candidate who has experience with data analytics tools
- A leader who inspires others and who can make quick decisions

This is a role that combines creativity with strategy. The company that you'll work for values work-life balance and offers benefits that include remote work options.

If you're a person who loves challenges and who wants to make a real impact, apply today!`,
      
      questions: [
        {
          type: "mc",
          q: "The job requires someone who:",
          options: [
            "Is creative, reliable, and self-confident",
            "Is shy and quiet",
            "Only speaks English"
          ],
          a: "Is creative, reliable, and self-confident",
          tip: "Check the first paragraph!",
          successtip: "✓ READING COMPREHENSION!\nFound the key characteristics! Excellent attention to detail! 👀"
        },
        {
          type: "mc",
          q: "How much experience is required?",
          options: [
            "At least 5 years",
            "At least 3 years",
            "No experience needed"
          ],
          a: "At least 5 years",
          successtip: "✓ DETAIL ORIENTED!\nYou caught the specific requirement! Great reading skills! 📊"
        },
        {
          type: "mc",
          q: "The position offers:",
          options: [
            "Excellent growth opportunities",
            "Only a basic salary",
            "No benefits"
          ],
          a: "Excellent growth opportunities",
          tip: "Look for what the job provides!",
          successtip: "✓ JOB BENEFITS!\nIdentified what makes this position attractive! Smart reading! 💼"
        },
        {
          type: "mc",
          q: "How many languages must candidates speak?",
          options: [
            "Two (English and Spanish)",
            "One (only English)",
            "Three languages"
          ],
          a: "Two (English and Spanish)",
          successtip: "✓ LANGUAGE SKILLS!\nCaught the bilingual requirement! Thorough reading! 🗣️"
        },
        {
          type: "mc",
          q: "The company values:",
          options: [
            "Work-life balance",
            "Working 24/7",
            "Only profit"
          ],
          a: "Work-life balance",
          tip: "Check what the company offers!",
          successtip: "✓ COMPANY CULTURE!\nUnderstand what the employer values! Cultural awareness! 🏢"
        },
        {
          type: "mc",
          q: "In 'someone who is fluent in English', WHO is:",
          options: [
            "Subject of 'is fluent'",
            "Object of 'is fluent'",
            "Not necessary"
          ],
          a: "Subject of 'is fluent'",
          tip: "WHO performs the action!",
          successtip: "✓ GRAMMAR ANALYSIS!\nUnderstand the structure while reading! Advanced skill! 📚"
        },
        {
          type: "mc",
          q: "The ad uses 'campaigns that reach millions'. This means:",
          options: [
            "The campaigns are very successful and large-scale",
            "The campaigns are small",
            "The campaigns are expensive"
          ],
          a: "The campaigns are very successful and large-scale",
          successtip: "✓ CONTEXT CLUES!\nUnderstand what the description implies! Excellent inference! 🎯"
        },
        {
          type: "mc",
          q: "What type of person should apply?",
          options: [
            "Someone who loves challenges and wants to make an impact",
            "Someone who prefers routine work",
            "Someone who avoids responsibility"
          ],
          a: "Someone who loves challenges and wants to make an impact",
          tip: "Check the final paragraph!",
          successtip: "✓ TARGET AUDIENCE!\nIdentified the ideal candidate profile! Strategic reading! 🎯"
        },
        {
          type: "mc",
          q: "How many defining relative clauses with WHO/THAT are in this ad?",
          options: [
            "More than 10",
            "Only 2",
            "None"
          ],
          a: "More than 10",
          tip: "Count all the WHO and THAT clauses!",
          successtip: "✓ GRAMMAR AWARENESS!\nRecognized the extensive use of relative clauses! Analytical! 🔍"
        },
        {
          type: "mc",
          q: "The overall tone of this job ad is:",
          options: [
            "Enthusiastic and inviting",
            "Cold and demanding",
            "Desperate and urgent"
          ],
          a: "Enthusiastic and inviting",
          successtip: "✓ TONE ANALYSIS!\nUnderstand the communication style! Professional reading comprehension! ⭐"
        }
      ]
    },

    {
      id: "m9-u15-review",
      title: "🔄 Unit Review: Defining Relative Clauses",
      context: `**UNIT 15 REVIEW - Being Specific with WHO and THAT**

Let's review everything you've learned:

✅ **WHO** = for people (someone who is reliable)
✅ **THAT** = for things (a job that is challenging)  
✅ **THAT** can also = for people (less precise than WHO)
✅ **Subject pronouns** = cannot drop (The person WHO works here)
✅ **Object pronouns** = can drop (The job [that] I want)
✅ **Never use WHAT** as a relative pronoun!

Now let's practice everything together!`,
      
      questions: [
        {
          type: "mc",
          q: "REVIEW: Which is correct?",
          options: [
            "I need someone who can help",
            "I need someone what can help",
            "I need someone where can help"
          ],
          a: "I need someone who can help",
          successtip: "✓ WHO FOR PEOPLE!\nPerfect! Never WHAT or WHERE! 👤"
        },
        {
          type: "mc",
          q: "REVIEW: Which can you drop the pronoun from?",
          options: [
            "The book (that) I read",
            "The book that costs $20",
            "Both are the same"
          ],
          a: "The book (that) I read",
          tip: "Object vs subject!",
          successtip: "✓ OBJECT PRONOUN!\nTHAT is the object - can drop it! Subject pronouns stay! 📚"
        },
        {
          type: "match",
          q: "REVIEW: Match the personality traits:",
          pairs: [
            { left: "Reliable", right: "You can depend on them" },
            { left: "Conscientious", right: "Does quality work carefully" },
            { left: "Humble", right: "Not arrogant or boastful" }
          ],
          a: "completed",
          successtip: "✓ VOCABULARY MASTERY!\nYou remember all the personality traits! Excellent! 🌟"
        },
        {
          type: "input",
          q: "REVIEW: Complete: She's a manager _____ everyone respects.",
          a: "who",
          alts: ["that", ""],
          tip: "Manager = person!",
          successtip: "✓ FLEXIBLE OPTIONS!\nWHO, THAT, or drop it - all work! WHO is most precise! 👔"
        },
        {
          type: "order",
          q: "REVIEW: Create: looking / am / challenges / I / opportunities / for / offer / that",
          words: ["I", "am", "looking", "for", "opportunities", "that", "offer", "challenges"],
          a: "I am looking for opportunities that offer challenges",
          extra: ["who", "which"],
          successtip: "✓ CAREER LANGUAGE!\nPerfect sentence for professional contexts! 💼"
        },
        {
          type: "mc",
          q: "REVIEW: What's wrong with 'The people what I met'?",
          options: [
            "Should be WHO or THAT, not WHAT",
            "Nothing is wrong",
            "Should be WHERE"
          ],
          a: "Should be WHO or THAT, not WHAT",
          successtip: "✓ ERROR DETECTION!\nWHAT is never a relative pronoun! Use WHO/THAT! ✅"
        },
        {
          type: "match",
          q: "REVIEW: Match job vocabulary:",
          pairs: [
            { left: "A job that is demanding", right: "Requires hard work" },
            { left: "A job that is rewarding", right: "Gives satisfaction" },
            { left: "A job that offers growth", right: "Opportunities to advance" }
          ],
          a: "completed",
          successtip: "✓ JOB DESCRIPTIONS!\nYou can describe different types of work! Professional! 📊"
        },
        {
          type: "input",
          q: "REVIEW: Most natural completion: The project _____ finished was successful.",
          a: "we",
          alts: ["that we", "which we"],
          tip: "Drop THAT for natural speech!",
          successtip: "✓ NATURAL ENGLISH!\nDropping object THAT sounds most fluent! Native-like! 🗣️"
        },
        {
          type: "mc",
          q: "REVIEW: For formal academic writing about things, prefer:",
          options: [
            "WHICH",
            "THAT",
            "WHO"
          ],
          a: "WHICH",
          tip: "Academic style uses WHICH more!",
          successtip: "✓ FORMAL STYLE!\nWHICH is preferred in academic and formal writing! 🎓"
        },
        {
          type: "match",
          q: "FINAL REVIEW: Identify the structure:",
          pairs: [
            { left: "People who work hard", right: "WHO subject - keep it" },
            { left: "The job I want", right: "THAT object - dropped" },
            { left: "A career that fits me", right: "THAT subject - keep it" }
          ],
          a: "completed",
          successtip: "✓ COMPLETE MASTERY!\n🏆 You've mastered Unit 15! WHO, THAT, all rules! Champion! 🏆"
        }
      ]
    }
  ]
};
